import React, { useState, useEffect, useRef } from 'react';
import { User } from '../../services/authService';
import PageHeader from '../common/PageHeader';
import {
  breakdownNumber,
  getAllCharacterMappings,
  getPictureAssociations
} from '../../utils/memoryTechniqueUtils';
import { 
  generateStory, 
  generateStoryImage,
  createEntry,
  getUserEntries,
  getEntryById,
  updateEntry,
  deleteEntry,
  uploadImages,
  MemoryTechniqueEntry
} from '../../services/memoryTechniqueService';
import './MemoryTechnique.css';

interface MemoryTechniqueProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

const MemoryTechnique: React.FC<MemoryTechniqueProps> = ({ user, onToggleSidebar }) => {
  const [inputNumber, setInputNumber] = useState('');
  const [pairs, setPairs] = useState<number[]>([]);
  const [pictureAssociations, setPictureAssociations] = useState<Array<{ number: number; picture: string }>>([]);
  const [story, setStory] = useState('');
  const [isGeneratingStory, setIsGeneratingStory] = useState(false);
  const [images, setImages] = useState<Array<{data: string, mimeType: string, alt?: string}>>([]);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showCharacterReference, setShowCharacterReference] = useState(false);
  const [savedEntries, setSavedEntries] = useState<MemoryTechniqueEntry[]>([]);
  const [editingEntryId, setEditingEntryId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showSavedEntries, setShowSavedEntries] = useState(false);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const menuRef = useRef<HTMLDivElement>(null);

  const characterMappings = getAllCharacterMappings();

  // Load saved entries on mount
  useEffect(() => {
    if (user?.id) {
      loadSavedEntries();
    }
  }, [user?.id]);

  useEffect(() => {
    if (inputNumber.trim()) {
      const numberPairs = breakdownNumber(inputNumber);
      setPairs(numberPairs);
      const associations = getPictureAssociations(numberPairs);
      setPictureAssociations(associations);
    } else {
      setPairs([]);
      setPictureAssociations([]);
      setStory('');
      setImages([]);
      setImageUrls([]);
    }
  }, [inputNumber]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const handleGenerateStory = async () => {
    if (pairs.length === 0 || pictureAssociations.length === 0) {
      return;
    }

    setIsGeneratingStory(true);
    try {
      const pictures = pictureAssociations.map(a => a.picture);
      const generatedStory = await generateStory({
        pairs,
        pictures,
        originalNumber: inputNumber
      });
      setStory(generatedStory);
    } catch (error) {
      console.error('Error generating story:', error);
      setStory('Failed to generate story. Please try again.');
    } finally {
      setIsGeneratingStory(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!story.trim()) {
      return;
    }

    setIsGeneratingImage(true);
    try {
      const generatedImages = await generateStoryImage(story, inputNumber);
      setImages(generatedImages);
    } catch (error) {
      console.error('Error generating image:', error);
      alert('Failed to generate image. Please try again.');
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const handleClear = () => {
    setInputNumber('');
    setPairs([]);
    setPictureAssociations([]);
    setStory('');
    setImages([]);
    setImageUrls([]);
    setEditingEntryId(null);
  };

  const loadSavedEntries = async () => {
    if (!user?.id) return;
    
    try {
      const response = await getUserEntries(user.id);
      if (response.success && response.entries) {
        setSavedEntries(response.entries);
      }
    } catch (error) {
      console.error('Error loading saved entries:', error);
    }
  };

  const handleSaveEntry = async () => {
    if (!user?.id || !inputNumber.trim() || !story.trim()) {
      return;
    }

    setIsSaving(true);
    try {
      let uploadedUrls: string[] = [...imageUrls];

      // Upload new images (base64 data) to Firebase Storage
      const newImages = images.filter(img => img.data && !img.data.startsWith('http'));
      if (newImages.length > 0) {
        // Determine entry ID for upload path (use editingEntryId or temporary ID)
        const tempId = editingEntryId || Date.now();
        const newUrls = await uploadImages(newImages, tempId);
        uploadedUrls = [...uploadedUrls, ...newUrls];
      }

      // Keep existing URLs from images that are already URLs
      const existingUrls = images
        .filter(img => img.data && img.data.startsWith('http'))
        .map(img => img.data);
      uploadedUrls = [...existingUrls, ...uploadedUrls.filter(url => !existingUrls.includes(url))];

      const entryData = {
        user_id: user.id,
        number: inputNumber,
        story: story,
        pairs: pairs,
        picture_associations: pictureAssociations,
        image_urls: uploadedUrls
      };

      let entryId = editingEntryId;
      if (editingEntryId) {
        // Update existing entry
        await updateEntry(editingEntryId, entryData);
      } else {
        // Create new entry
        const response = await createEntry(user.id, entryData);
        if (response.success && response.id) {
          entryId = response.id;
          setEditingEntryId(entryId);
        }
      }

      // Update local state with uploaded URLs
      setImageUrls(uploadedUrls);
      
      // Update images to use URLs instead of base64
      if (uploadedUrls.length > 0) {
        setImages(uploadedUrls.map(url => ({
          data: url,
          mimeType: 'image/png',
          alt: `Memory visualization for ${inputNumber}`
        })));
      }

      // Reload entries list
      await loadSavedEntries();
    } catch (error) {
      console.error('Error saving entry:', error);
      alert('Failed to save entry. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLoadEntry = async (entryId: number) => {
    try {
      const response = await getEntryById(entryId);
      if (response.success && response.entry) {
        const entry = response.entry;
        setInputNumber(entry.number);
        setStory(entry.story);
        setPairs(entry.pairs || []);
        setPictureAssociations(entry.picture_associations || []);
        setEditingEntryId(entry.id || null);
        
        // Set image URLs and images for display
        const urls = entry.image_urls || [];
        setImageUrls(urls);
        if (urls.length > 0) {
          setImages(urls.map(url => ({
            data: url,
            mimeType: 'image/png',
            alt: `Memory visualization for ${entry.number}`
          })));
        } else {
          setImages([]);
        }
        
        setShowSavedEntries(false);
      }
    } catch (error) {
      console.error('Error loading entry:', error);
      alert('Failed to load entry. Please try again.');
    }
  };

  const handleDeleteEntry = async (entryId: number) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) {
      return;
    }

    try {
      await deleteEntry(entryId);
      await loadSavedEntries();
      
      // Clear form if deleting the currently edited entry
      if (editingEntryId === entryId) {
        handleClear();
      }
    } catch (error) {
      console.error('Error deleting entry:', error);
      alert('Failed to delete entry. Please try again.');
    }
  };

  const rightActions = (
    <div className="memory-technique-header-actions" ref={menuRef}>
      <button
        className="memory-technique-menu-btn"
        onClick={() => setShowMenu(!showMenu)}
        title="Menu"
      >
        ⋯
      </button>
      {showMenu && (
        <div className="memory-technique-menu-dropdown">
          <button
            className="memory-technique-menu-item"
            onClick={() => {
              setShowCharacterReference(!showCharacterReference);
              setShowMenu(false);
            }}
          >
            {showCharacterReference ? 'Hide' : 'Show'} Character Reference
          </button>
          <button
            className="memory-technique-menu-item"
            onClick={() => {
              handleClear();
              setShowMenu(false);
            }}
          >
            Clear All
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="memory-technique-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="page-header-center-title">/memory-technique</h1>}
        rightActions={rightActions}
      />

      <div className="memory-technique-content">
        {/* Character Reference Section */}
        {showCharacterReference && (
          <div className="memory-technique-reference-section">
            <h2 className="memory-technique-section-title">Character Mappings (0-9)</h2>
            <div className="memory-technique-character-grid">
              {characterMappings.map(({ digit, character }) => (
                <div key={digit} className="memory-technique-character-item">
                  <span className="memory-technique-character-digit">{digit}</span>
                  <span className="memory-technique-character-value">{character}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Input Section */}
        <div className="memory-technique-input-section">
          <label htmlFor="number-input" className="memory-technique-label">
            Enter Number
          </label>
          <input
            id="number-input"
            type="text"
            className="memory-technique-input"
            value={inputNumber}
            onChange={(e) => setInputNumber(e.target.value)}
            placeholder="e.g., 12345"
          />
        </div>

        {/* Breakdown Display */}
        {pairs.length > 0 && (
          <div className="memory-technique-breakdown-section">
            <h2 className="memory-technique-section-title">Breakdown</h2>
            <div className="memory-technique-breakdown">
              <div className="memory-technique-breakdown-pairs">
                {pairs.map((pair, index) => (
                  <div key={index} className="memory-technique-pair-item">
                    <span className="memory-technique-pair-number">{pair.toString().padStart(2, '0')}</span>
                    <span className="memory-technique-pair-separator">→</span>
                    <span className="memory-technique-pair-picture">{pictureAssociations[index]?.picture || ''}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Story Section */}
        {pairs.length > 0 && (
          <div className="memory-technique-story-section">
            <div className="memory-technique-story-header">
              <h2 className="memory-technique-section-title">Story</h2>
              <div className="memory-technique-story-actions">
                <button
                  className="memory-technique-generate-btn"
                  onClick={handleGenerateStory}
                  disabled={isGeneratingStory}
                >
                  {isGeneratingStory ? 'Generating...' : 'Generate Story'}
                </button>
                {story && (
                  <button
                    className="memory-technique-generate-btn"
                    onClick={handleGenerateImage}
                    disabled={isGeneratingImage}
                  >
                    {isGeneratingImage ? 'Generating...' : 'Generate Image'}
                  </button>
                )}
              </div>
            </div>
            {story && (
              <>
                <textarea
                  className="memory-technique-story-textarea"
                  value={story}
                  onChange={(e) => setStory(e.target.value)}
                  placeholder="Story will appear here after generation..."
                  rows={6}
                />
                <div className="memory-technique-save-section">
                  <button
                    className="memory-technique-save-btn"
                    onClick={handleSaveEntry}
                    disabled={isSaving || !inputNumber.trim() || !story.trim()}
                  >
                    {isSaving ? 'Saving...' : editingEntryId ? 'Update Entry' : 'Save Entry'}
                  </button>
                </div>
              </>
            )}
            {images.length > 0 && (
              <div className="memory-technique-images-section">
                <h3 className="memory-technique-section-title">Visualization</h3>
                <div className="memory-technique-images-grid">
                  {images.map((img, index) => (
                    <div key={index} className="memory-technique-image-container">
                      <img
                        src={img.data.startsWith('http') ? img.data : `data:${img.mimeType};base64,${img.data}`}
                        alt={img.alt || `Memory visualization ${index + 1}`}
                        className="memory-technique-image"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Saved Entries Section */}
        {savedEntries.length > 0 && (
          <div className="memory-technique-saved-section">
            <button
              className="memory-technique-saved-toggle"
              onClick={() => setShowSavedEntries(!showSavedEntries)}
            >
              <span className="memory-technique-section-title">/saved-entries</span>
              <span className="memory-technique-toggle-arrow">
                {showSavedEntries ? '▼' : '▶'}
              </span>
            </button>
            {showSavedEntries && (
              <div className="memory-technique-saved-content">
                {savedEntries.map((entry) => (
                  <div key={entry.id} className="memory-technique-entry-card">
                    <div className="memory-technique-entry-header">
                      <div className="memory-technique-entry-info">
                        <span className="memory-technique-entry-number">{entry.number}</span>
                        <span className="memory-technique-entry-date">
                          {entry.created_at ? new Date(entry.created_at).toLocaleDateString() : ''}
                        </span>
                      </div>
                      <div className="memory-technique-entry-actions">
                        <button
                          className="memory-technique-entry-btn"
                          onClick={() => entry.id && handleLoadEntry(entry.id)}
                          title="Edit"
                        >
                          Edit
                        </button>
                        <button
                          className="memory-technique-entry-btn memory-technique-entry-btn-delete"
                          onClick={() => entry.id && handleDeleteEntry(entry.id)}
                          title="Delete"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    <div className="memory-technique-entry-story-preview">
                      {entry.story.substring(0, 100)}{entry.story.length > 100 ? '...' : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MemoryTechnique;

